import { useQuery } from '@tanstack/react-query';
import { alertThresholdService } from '../services/endpoints/alertThresholdService';
import { AlertThreshold } from '../types';

// ── Severity type ─────────────────────────────────────────────────────────────

export type AlertLevel = 'red' | 'orange' | 'green' | null;

// ── Core evaluator ────────────────────────────────────────────────────────────

/**
 * Evaluate a single numeric metric value against all matching threshold records.
 *
 * Convention:
 *  - High-value alerts (BP, HR, glucose): criticalMin / warningMin are set.
 *    value ≥ criticalMin → red;  value ≥ warningMin → orange
 *  - Low-value alerts  (sleep hrs/quality): criticalMax / warningMax are set.
 *    value ≤ criticalMax → red;  value ≤ warningMax → orange
 *
 * Returns null when no matching threshold is found or value is absent.
 */
export function evaluateMetric(
  key: string,
  value: number | null | undefined,
  thresholds: AlertThreshold[],
): AlertLevel {
  if (value == null) return null;
  const matching = thresholds.filter(t => t.metricKey === key);
  if (matching.length === 0) return null;

  let worst: AlertLevel = 'green';

  for (const t of matching) {
    // Critical check (higher priority — short-circuit on first red)
    if (
      (t.criticalMin != null && value >= t.criticalMin) ||
      (t.criticalMax != null && value <= t.criticalMax)
    ) {
      return 'red';
    }
    // Warning check
    if (
      (t.warningMin != null && value >= t.warningMin) ||
      (t.warningMax != null && value <= t.warningMax)
    ) {
      worst = 'orange';
    }
  }

  return worst;
}

/**
 * Evaluate systolic + diastolic blood pressure together.
 * Returns the worst severity across both readings.
 */
export function evaluateBP(
  systolic: number | null | undefined,
  diastolic: number | null | undefined,
  thresholds: AlertThreshold[],
): AlertLevel {
  const sysLevel = evaluateMetric('systolic_bp', systolic, thresholds);
  const diaLevel = evaluateMetric('diastolic_bp', diastolic, thresholds);

  const levels: AlertLevel[] = [sysLevel, diaLevel];
  if (levels.includes('red'))    return 'red';
  if (levels.includes('orange')) return 'orange';
  if (levels.includes('green'))  return 'green';
  return null;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the cached alert threshold definitions.
 * Data is considered fresh for 1 hour — these records change rarely.
 */
export function useAlertThresholds(): { thresholds: AlertThreshold[] } {
  const { data: thresholds = [] } = useQuery<AlertThreshold[]>({
    queryKey: ['alert-thresholds'],
    queryFn:  alertThresholdService.getThresholds,
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
  });

  return { thresholds };
}
